import type { HTMLChakraProps, Textarea } from '@chakra-ui/react';
import { Input } from '@chakra-ui/react';
import { Field } from '@ui/components/ui/field';
import type {
  FieldError,
  FieldErrorsImpl,
  FieldValues,
  Merge,
} from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

interface FormFieldProps extends HTMLChakraProps<'div'> {
  name: string;
  label: string;
  placeholder?: string;
  ChakraComponent?: typeof Input | typeof Textarea;
  validators?: Parameters<typeof Controller>[0]['rules'];
  hidden?: boolean;
  disabled?: boolean;
}

export const FormField = ({
  name,
  label,
  placeholder,
  ChakraComponent = Input,
  validators,
  hidden,
  disabled,
  ...props
}: FormFieldProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const isRequired = !!validators?.required;

  return (
    <Controller
      name={name}
      control={control}
      rules={validators}
      disabled={disabled}
      render={({ field }) => (
        <Field
          label={label}
          invalid={!!errors[name]}
          errorText={getErrorText(errors[name])}
          hidden={hidden}
          required={isRequired}
          disabled={disabled}
          {...props}
        >
          <ChakraComponent
            placeholder={
              isRequired && placeholder ? placeholder + '*' : placeholder
            }
            {...field}
            value={field.value ?? ''}
            hidden={hidden}
          />
        </Field>
      )}
    />
  );
};

const getErrorText = <T extends FieldValues>(
  error: FieldError | Merge<FieldError, FieldErrorsImpl<T>> | undefined,
) => {
  const errorMessage = error?.message;
  return !errorMessage ? undefined : `${errorMessage}`;
};
